import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ClipboardCheck, Trophy } from "lucide-react";
import { toast } from "sonner";

export default function MatchResultDialog({ match, onSubmitted }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [team1Score, setTeam1Score] = useState("");
  const [team2Score, setTeam2Score] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!match || !user) return null;
  const isCreator = match.creator_id === user.id;
  const isAdmin = user.role === "admin";
  const isFull = match.players?.length >= match.max_players;
  const isCompleted = match.status === "completed";

  // Only show for full matches that aren't completed, and only for creator/admin
  if (isCompleted || !isFull || (!isCreator && !isAdmin)) return null;

  const players = match.players || [];
  const team1 = players.slice(0, 2);
  const team2 = players.slice(2, 4);

  const handleSubmit = async () => {
    const s1 = parseInt(team1Score);
    const s2 = parseInt(team2Score);
    if (isNaN(s1) || isNaN(s2) || s1 < 0 || s2 < 0) {
      toast.error("Enter valid scores");
      return;
    }
    if (s1 === s2) {
      toast.error("Padel matches can't end in a draw");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/matches/${match._id}/result`, {
        team1_score: s1,
        team2_score: s2,
        team1_players: team1.map(p => p.id),
        team2_players: team2.map(p => p.id),
      });
      toast.success("Result submitted! Rankings updated.");
      setOpen(false);
      if (onSubmitted) onSubmitted();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="submit-result-btn"
          className="w-full rounded-full bg-golden hover:bg-golden-500 text-midnight-500 text-sm gap-1.5 font-semibold"
          size="sm"
        >
          <ClipboardCheck size={14} /> Submit Result
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy size={18} className="text-golden" />
            Submit Match Result
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <p className="text-xs text-muted-foreground">
            {match.club_name} &mdash; {match.date} at {match.time}
            {match.mode === "competitive" && (
              <Badge className="ml-2 rounded-full text-[10px] bg-coral/10 text-coral border-coral/20">ELO will update</Badge>
            )}
          </p>

          {/* Team 1 */}
          <div className="p-4 rounded-xl bg-teal/5 border border-teal/15">
            <Label className="text-xs text-teal font-semibold uppercase tracking-wider mb-2 block">Team 1</Label>
            <div className="flex items-center gap-2 mb-3">
              {team1.map(p => (
                <Badge key={p.id} variant="secondary" className="rounded-full text-xs">
                  {p.name}
                </Badge>
              ))}
            </div>
            <Input
              data-testid="team1-score"
              type="number"
              min="0"
              placeholder="Score"
              value={team1Score}
              onChange={e => setTeam1Score(e.target.value)}
              className="h-12 text-center text-2xl font-black rounded-xl"
            />
          </div>

          <div className="text-center text-xs text-muted-foreground font-medium">VS</div>

          {/* Team 2 */}
          <div className="p-4 rounded-xl bg-coral/5 border border-coral/15">
            <Label className="text-xs text-coral font-semibold uppercase tracking-wider mb-2 block">Team 2</Label>
            <div className="flex items-center gap-2 mb-3">
              {team2.map(p => (
                <Badge key={p.id} variant="secondary" className="rounded-full text-xs">
                  {p.name}
                </Badge>
              ))}
            </div>
            <Input
              data-testid="team2-score"
              type="number"
              min="0"
              placeholder="Score"
              value={team2Score}
              onChange={e => setTeam2Score(e.target.value)}
              className="h-12 text-center text-2xl font-black rounded-xl"
            />
          </div>

          <Button
            data-testid="confirm-result-btn"
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full rounded-full bg-coral hover:bg-coral-600 text-white h-11 font-semibold"
          >
            {submitting ? "Submitting..." : "Confirm Result"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
